import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
// @ts-ignore
import MaskedInput from 'react-text-mask';
import { TextField, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import Stack from 'components/shared/Stack';
import { formatAddress, formatPhoneNumber } from 'utils/functions';
import { Lead } from 'typings';

interface LeadFormProps {
  lead: Lead;
  onSubmit: Function;
}

interface TextMaskCustomProps {
  inputRef: (ref: HTMLInputElement | null) => void;
}

function TextMaskCustom(props: TextMaskCustomProps) {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
      placeholderChar={'\u2000'}
      showMask
    />
  );
}

const FormContainer = styled('form')`
  .inline-form-row {
    > * + * {
      margin-left: ${(props) => props.theme.spacing(2)};
    }
  }

  .btn-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: ${(props) => props.theme.spacing(1, 0, 0)};
  }
`;

/* Edit Lead Details Component */
const EditLeadDetails = ({ lead, onSubmit }: LeadFormProps) => {
  const [leadFormAddress, setLeadFormAddress] = useState({});
  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      phone: formatPhoneNumber(lead.phone || undefined) || '',
      email: lead.email || '',
      address: formatAddress(lead.street, lead.city, lead.state, lead.postalCode) || '',
    },
  });
  const autoCompleteRef = useRef(null);
  const theme = useTheme();
  const { address } = watch();

  useEffect(() => {
    let autoComplete: any;

    function handleAddressSelect() {
      const { formatted_address: formattedAddress, address_components: addressComponents } =
        autoComplete.getPlace();

      if (formattedAddress) {
        setValue('address', formattedAddress);
      }

      if (addressComponents) {
        let streetNumber, streetName, city, state, country, postalCode;
        let street = null;

        addressComponents.forEach((item: any) => {
          if (item['types'].includes('locality')) {
            city = item['long_name'];
          } else if (item['types'].includes('administrative_area_level_1')) {
            state = item['short_name'];
          } else if (item['types'].includes('street_number')) {
            streetNumber = item['short_name'];
          } else if (item['types'].includes('route')) {
            streetName = item['long_name'];
          } else if (item['types'].includes('country')) {
            country = item['long_name'];
          } else if (item['types'].includes('postal_code')) {
            postalCode = item['short_name'];
          }
        });

        if (streetNumber && !streetName) {
          street = streetNumber;
        } else if (streetName && !streetNumber) {
          street = streetName;
        } else if (streetName && streetNumber) {
          street = `${streetNumber} ${streetName}`;
        }

        setLeadFormAddress({
          street,
          city,
          state,
          country,
          postalCode,
        });
      }
    }

    function initializeAddressAutoComplete() {
      const {
        google: { maps },
      } = window as any;
      autoComplete = new maps.places.Autocomplete(autoCompleteRef.current, {
        types: ['geocode'],
        componentRestrictions: { country: ['us', 'ca'] },
      });

      autoComplete.setFields(['address_component', 'formatted_address']);
      autoComplete.addListener('place_changed', handleAddressSelect);
    }

    initializeAddressAutoComplete();
  }, [setValue]);

  const saveFormData = handleSubmit((data) => {
    let addressOverrides;

    // if we have an entered address, we assume everything before the first comma is the street;
    // this allows the user to override the value for street and to capture cases where the address is entered without
    // using the autocomplete (e.g. manually typed in or copy/pasted)
    if (data.address) {
      // NOTE: what follows is desired behavior but has some known side-effects;
      // this will allow the address value to be edited completely independently of the autocomplete, but in these cases,
      // will not update anything except for the street field; will revisit as needed
      const addressParts = data.address.split(',');
      addressOverrides = { street: addressParts[0] };

      // if there is nothing in the address field, we'd like to clear our the address components
    } else {
      addressOverrides = {
        street: null,
        city: null,
        state: null,
        country: null,
        postalCode: null,
      };
    }

    const formData: any = { ...data, ...leadFormAddress, ...addressOverrides };

    // the actual `address` property is not needed anymore since we update `formData` with the individual address components
    delete formData.address;

    onSubmit(formData);
  });

  const handleAddressKeyDown = (event: any) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  return (
    <FormContainer onSubmit={saveFormData}>
      <Stack space={theme.spacing(1.5)}>
        <div className="horizontal-space inline-form-row">
          <Controller
            as={TextField}
            name="firstName"
            control={control}
            fullWidth
            autoFocus
            label="first name"
          />
          <Controller
            as={TextField}
            name="lastName"
            control={control}
            fullWidth
            label="last name"
          />
        </div>
        <Controller
          as={TextField}
          name="phone"
          control={control}
          fullWidth
          label="phone"
          InputProps={{
            inputComponent: TextMaskCustom as any,
          }}
        />
        <Controller
          as={TextField}
          name="email"
          control={control}
          fullWidth
          label="email"
          type="email"
        />
        <Controller
          as={TextField}
          name="address"
          control={control}
          value={address}
          fullWidth
          label="address"
          inputRef={autoCompleteRef}
          onKeyDown={handleAddressKeyDown}
        />
        <div className="btn-wrapper">
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </div>
      </Stack>
    </FormContainer>
  );
};

export default EditLeadDetails;
